import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Loader2, Plus, ArrowRight, ShieldCheck, ShoppingBag, Trash2 } from 'lucide-react'
import { useTenant } from '../contexts/TenantContext'
import { useBasket } from '../contexts/BasketContext'
import { useCurrency } from '../contexts/CurrencyContext'
import { getCategories } from '../utils/tebex'

// "You might also like" pool, shuffled per cart open.
const SUGGEST_POOL = [6123186, 6123188, 6123183, 6145337, 6145338, 6145336, 6146824]
const SUGGEST_LABELS = { 6145337: 'Gold VIP', 6145338: 'Platinum VIP', 6145336: 'Silver VIP', 6146824: 'Diamond VIP' }
function shuffle(arr) {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]] }
  return a
}

// Site-wide cart slide-over. Items are the LOCAL cart from BasketContext;
// sign-in + Tebex sync happen via the checkout button.
export default function CartDrawer() {
  const tenant = useTenant()
  const navigate = useNavigate()
  const token = tenant && tenant.tebex && tenant.tebex.publicToken
  const { enabled, cartOpen, closeCart, items, subtotal, busy, actionError, remove, addToCart } = useBasket()
  const { format, isApprox } = useCurrency()
  const [pkgById, setPkgById] = useState(null)
  const [order, setOrder] = useState([])

  useEffect(() => {
    if (!cartOpen || pkgById || !token) return
    let cancelled = false
    getCategories(token)
      .then((cats) => { if (!cancelled) setPkgById(new Map((cats || []).flatMap((c) => c.packages || []).map((p) => [String(p.id), p]))) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [cartOpen, pkgById, token])

  useEffect(() => { if (cartOpen) setOrder(shuffle(SUGGEST_POOL)) }, [cartOpen])

  useEffect(() => {
    if (!cartOpen) return
    const onKey = (e) => { if (e.key === 'Escape') closeCart() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [cartOpen, closeCart])

  if (!enabled) return null

  const inCart = new Set(items.map((p) => p.id))
  const hasSub = items.some((p) => p.sub)
  const suggestions = pkgById
    ? order.map((id) => pkgById.get(String(id))).filter((p) => p && !inCart.has(p.id) && !(hasSub && p.type === 'subscription')).slice(0, 2)
    : []
  const count = items.reduce((n, p) => n + (p.quantity || 1), 0)

  return (
    <div className={'mw-public !bg-transparent fixed inset-0 z-50 ' + (cartOpen ? '' : 'pointer-events-none')} aria-hidden={!cartOpen}>
      <div className={'absolute inset-0 bg-black/70 backdrop-blur-[2px] transition-opacity duration-300 ' + (cartOpen ? 'opacity-100' : 'opacity-0')} onClick={closeCart} />
      <aside
        role="dialog"
        aria-label="Your cart"
        className={'absolute top-0 right-0 sm:top-3 sm:right-3 sm:bottom-3 h-full sm:h-auto w-full max-w-[440px] bg-[#0e1015] sm:border sm:border-white/[0.08] sm:rounded-[28px] overflow-hidden shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)] flex flex-col transition-transform duration-500 ease-[cubic-bezier(.2,.7,.2,1)] ' + (cartOpen ? 'translate-x-0' : 'translate-x-[110%]')}
      >
        <div className="px-6 h-[72px] flex items-center">
          <h2 className="mw-display text-white text-[26px]">Cart</h2>
          {count > 0 && <span className="ml-3 mw-badge bg-white/[0.08] text-[#c4c6cb] tabular-nums">{count} {count === 1 ? 'item' : 'items'}</span>}
          <button onClick={closeCart} aria-label="Close cart" className="ml-auto mw-icon-btn !bg-white/[0.06] hover:!bg-white/[0.12] hover:!text-white"><X /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-3">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-6 pb-10">
              <div className="w-16 h-16 rounded-full bg-white/[0.05] flex items-center justify-center mb-5"><ShoppingBag className="w-6 h-6 text-[#8b8d94]" /></div>
              <p className="text-[17px] text-white font-medium">Your cart is empty</p>
              <p className="mt-1.5 text-[14px] text-[#8b8d94]">Gems, VIP and perks are waiting.</p>
              <button onClick={() => { closeCart(); navigate('/store') }} className="mt-6 mw-btn mw-btn-ghost mw-btn-sm">Browse the store</button>
            </div>
          ) : (
            <ul className="space-y-2">
              {items.map((p) => (
                <li key={p.id} className="flex items-center gap-4 p-2.5 pr-4 rounded-[20px] bg-white/[0.035]">
                  <div className="w-16 h-16 shrink-0 rounded-[14px] bg-black overflow-hidden">{p.image && <img src={p.image} alt="" className="w-full h-full object-cover" />}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] text-white leading-snug">{p.name}</p>
                    <p className="mt-1 text-[13px] text-[#8b8d94] tabular-nums">
                      {p.quantity > 1 ? p.quantity + ' x ' : ''}{format(p.price)}{p.sub ? ' /mo' : ''}
                    </p>
                  </div>
                  <button onClick={() => remove(p.id)} aria-label={'Remove ' + p.name} className="w-9 h-9 rounded-full flex items-center justify-center text-[#5d6068] hover:text-[#ff6b7d] hover:bg-[#e5233b]/10 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </li>
              ))}
            </ul>
          )}

          {suggestions.length > 0 && items.length > 0 && (
            <div className="px-3 pt-8 pb-4">
              <p className="text-[13px] font-medium text-[#8b8d94] mb-3">You might also like</p>
              <div className="space-y-2">
                {suggestions.map((p) => (
                  <div key={p.id} className="flex items-center gap-3">
                    <div className="w-11 h-11 shrink-0 rounded-xl bg-black overflow-hidden">{p.image && <img src={p.image} alt="" className="w-full h-full object-cover" />}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-white truncate">{SUGGEST_LABELS[p.id] || p.name}</p>
                      <p className="text-[12px] text-[#8b8d94] tabular-nums">{format(p.total_price != null ? p.total_price : p.base_price)}{p.type === 'subscription' ? ' /mo' : ''}</p>
                    </div>
                    <button onClick={() => addToCart(p)} aria-label={'Add ' + p.name} className="mw-icon-btn"><Plus /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="m-3 p-5 rounded-[22px] bg-white/[0.04]">
          {actionError && <p className="text-[13px] text-[#ff6b7d] mb-4" role="alert">{actionError}</p>}
          <div className="flex items-baseline justify-between">
            <span className="text-[14px] text-[#8b8d94]">Subtotal</span>
            <span className="text-[22px] text-white tabular-nums">{format(subtotal)}</span>
          </div>
          {isApprox && <p className="mt-1 text-[12px] text-[#5d6068]">Approximate - you're charged in USD at checkout.</p>}
          {busy ? (
            <div className="mt-5 h-12 flex items-center justify-center gap-2 text-[#8b8d94] text-[14px]"><Loader2 className="w-4 h-4 animate-spin" /> Finishing checkout</div>
          ) : (
            <button onClick={() => { closeCart(); navigate('/signin') }} disabled={items.length === 0} className="mt-5 mw-btn mw-btn-primary w-full">
              Checkout <ArrowRight className="mw-arrow" />
            </button>
          )}
          <p className="mt-3 flex items-center justify-center gap-1.5 text-[12px] text-[#5d6068]"><ShieldCheck className="w-3.5 h-3.5" /> Secure payment via Tebex</p>
        </div>
      </aside>
    </div>
  )
}
